import { useEffect, useState } from "react";
import { t } from "i18next";
import { message, Select } from "antd";
import { useSetState } from "ahooks";
import { LinkedAccount } from "@store/linkedAccountSlice";
import { FormBody, type PostToTikTokFormState } from "./FormBody";
import {
  AssetProductInfoResponse,
  queryAssetProductInfo,
  submitPublish,
  UserPublishResultResponse,
} from "@apis/http/api";
import { useSerializeCallback } from "@kalodata/tools";
import { useLinkAccount } from "@pages/me/hooks/useLinkAccount";
import { ModalHeader } from "./ModalHeader";
import { ModalFooter } from "./ModalFooter";
import { PostRecordProbe } from "./PostRecordProbe";
import { isDebugEnabled, MOCK_CASES } from "./debugMocks";
import { UserPublishSubmitRequestPublishMode } from "@apis/generated/openapi";
import { getTitleBannedCharsInValue, TITLE_BANNED_REGEX } from "./titleConstraints";

export interface TikTokProductFormProps {
  open: boolean;
  assetId: number;
  account: LinkedAccount;
  onClose: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}

// 比较表单字段是否有差异（仅比较用户可编辑/可见的字段，跳过 postRecordId）
const COMPARABLE_FIELDS: Array<keyof PostToTikTokFormState> = [
  "description",
  "title",
  "productId",
  "needPrecheck",
  "publishMode",
  "publishTime",
];

const isFormStateDirty = (current: PostToTikTokFormState, baseline: PostToTikTokFormState | null) => {
  if (!baseline) return false;
  return COMPARABLE_FIELDS.some((key) => current[key] !== baseline[key]);
};

const buildInitialFormState = (productInfo?: AssetProductInfoResponse | null): PostToTikTokFormState => ({
  publishMode: UserPublishSubmitRequestPublishMode.NOW,
  description: "",
  title: "",
  productId: productInfo?.productId ?? "",
  productTitle: productInfo?.productTitle ?? "",
  productImage: productInfo?.imageUrl ?? "",
  salesCount: productInfo?.salesCount,
  postRecordId: undefined,
  needPrecheck: false,
  publishTime: undefined,
});

// Debug 用：当不为 null 时跳过真实 submit，直接走 PostRecordProbe + mockResult。
const DEBUG_SENTINEL_RECORD_ID = -1;

export const TikTokProductForm = ({ open, onClose, assetId, account, onDirtyChange }: TikTokProductFormProps) => {
  const linkId = account.id!;
  const [formState, setFormState] = useSetState<PostToTikTokFormState>(() => buildInitialFormState());
  const [baselineState, setBaselineState] = useState<PostToTikTokFormState | null>(null);
  const [mockResult, setMockResult] = useState<UserPublishResultResponse | null>(null);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [titleTouched, setTitleTouched] = useState(false);
  const hasPostRecordId = !!formState.postRecordId;
  const reconnectAccount = useLinkAccount();
  const scheduleTimeMissing =
    formState.publishMode === UserPublishSubmitRequestPublishMode.SCHEDULE && !formState.publishTime;
  const submittedTitle = formState.title.trim() ? formState.title : formState.productTitle || "";
  const titleHasBannedChars = TITLE_BANNED_REGEX.test(submittedTitle);
  const titleBannedCharsDisplay = titleHasBannedChars ? getTitleBannedCharsInValue(submittedTitle) : "";
  const baseCanPost = !!assetId && !!formState.productId && !!formState.description.trim();
  const canPost = baseCanPost;
  const getAssetRelatedProductInfo = useSerializeCallback(async () => {
    const res = await queryAssetProductInfo({ assetId, linkId });
    const initial = buildInitialFormState(res);
    setFormState(initial);
    setBaselineState(initial);
    setSubmitAttempted(false);
    setTitleTouched(false);
  });

  useEffect(() => {
    if (open) {
      getAssetRelatedProductInfo();
    } else {
      setBaselineState(null);
    }
  }, [open, getAssetRelatedProductInfo]);

  // 仅在表单视图下计算 dirty；进入 PostRecordProbe 后视为已提交，无需二次确认。
  const isDirty = !hasPostRecordId && isFormStateDirty(formState, baselineState);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    return () => {
      onDirtyChange?.(false);
    };
  }, [onDirtyChange]);

  const handleSubmitProductInfo = useSerializeCallback(async () => {
    setSubmitAttempted(true);
    if (scheduleTimeMissing || titleHasBannedChars) {
      return;
    }
    const scheduledPublishTime =
      formState.publishMode === UserPublishSubmitRequestPublishMode.SCHEDULE ? formState.publishTime : undefined;
    const submitted = await submitPublish({
      linkId,
      assetId,
      publishMode: formState.publishMode,
      productId: formState.productId,
      productTitle: formState.productTitle || "",
      productImage: formState.productImage,
      title: submittedTitle,
      description: formState.description,
      needPrecheck: formState.needPrecheck,
      publishTime: scheduledPublishTime,
    });
    if (submitted.status === "INIT" && submitted.id) {
      setMockResult(null);
      setFormState({ postRecordId: submitted.id });
    } else {
      message.error(t(`Failed:${submitted.status || "UNKNOWN"}`));
    }
  });
  const submitting = handleSubmitProductInfo.isPending();
  const scheduledDateError = submitAttempted && scheduleTimeMissing;
  const titleError = (submitAttempted || titleTouched) && titleHasBannedChars;

  // 用户取消/失败回到表单：清掉 postRecordId / mock 让组件渲染回 FormBody，保留之前填写的字段。
  const handleBackToEdit = () => {
    setMockResult(null);
    setFormState({ postRecordId: undefined });
  };

  // 已发布失败重试：直接复用 submit 流程；新的 postRecordId 会替换旧值，
  // PostRecordProbe 的 effect 依赖 postRecordId 变化时会自动重启轮询。
  const handleRetryPosting = handleSubmitProductInfo;

  // Debug 入口：选中后跳过 submit，直接喂给 PostRecordProbe 渲染对应的 UI 分支。
  const handlePickMock = (label: string) => {
    const picked = MOCK_CASES.find((c) => c.label === label);
    if (!picked) return;
    setMockResult(picked.result);
    setFormState({ postRecordId: DEBUG_SENTINEL_RECORD_ID });
  };

  return (
    <div className="flex flex-col bg-kds-bg rounded-[12px]">
      <ModalHeader onClose={onClose} />
      {hasPostRecordId ? (
        <PostRecordProbe
          isScheduled={formState.publishMode === UserPublishSubmitRequestPublishMode.SCHEDULE}
          className="min-h-[628px]"
          postRecordId={formState.postRecordId!}
          retrying={handleRetryPosting.isPending()}
          reconnecting={reconnectAccount.isPending()}
          mockResult={mockResult ?? undefined}
          onSuccess={onClose}
          onBackToEdit={handleBackToEdit}
          onRetry={handleRetryPosting}
          onReconnect={reconnectAccount}
        />
      ) : (
        <>
          <FormBody
            className="flex min-h-[546px] flex-col"
            account={account}
            formState={formState}
            setFormState={setFormState}
            disabled={submitting}
            scheduledDateError={scheduledDateError}
            titleError={titleError}
            titleBannedCharsDisplay={titleBannedCharsDisplay}
            onTitleBlur={() => setTitleTouched(true)}
          />
          {isDebugEnabled() && (
            <div className="flex items-center gap-[8px] border-t border-dashed border-kds-border bg-kds-bg-soft px-[16px] py-[8px]">
              <span className="shrink-0 text-[12px] font-semibold text-kds-fg-soft">[DEBUG] Mock status:</span>
              <Select
                size="small"
                allowClear
                placeholder="Pick a state to preview..."
                className="flex-1"
                onChange={(label?: string) => label && handlePickMock(label)}
                options={MOCK_CASES.map((c) => ({ value: c.label, label: c.label }))}
              />
            </div>
          )}
          <ModalFooter
            onCancel={onClose}
            onPost={handleSubmitProductInfo}
            publishing={submitting}
            disabledCancel={submitting}
            disabledPost={!canPost}
          />
        </>
      )}
    </div>
  );
};
